/**
 * Per-route error boundary that catches rendering errors thrown by lazy-loaded
 * page components and displays a user-friendly fallback with a recovery action.
 *
 * React error boundaries require class components.
 *
 * A failed lazy chunk (the previous deploy's file, gone after a redeploy) is
 * not recoverable by re-rendering - React caches the rejected import - so that
 * case reloads the page instead of resetting state.
 *
 * @module components/shared/RouteErrorBoundary
 * @see TASK-MIG-ERROR-BOUNDARIES
 * @see TASK-BUGFIX-STALE-CHUNK-ERROR-PAGE-CHG-003
 */
import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Trans } from '@lingui/react/macro';
import { isChunkLoadError, reloadForStaleChunk } from '../../utils/staleChunkRecovery';
import { useBranding } from '../../branding/BrandingProvider';
import { DefaultBranding } from '../../branding/defaults';

/**
 * The installation product name for the notice below, and the BUILT-IN name
 * whenever there is nothing else - never an empty string and never "undefined"
 * (SPEC-PLATFORM-BRANDING RULE-012, RULE-001's fail-open half).
 *
 * `useBranding()` THROWS outside a `BrandingProvider`, deliberately: a surface
 * that silently fell back to the built-in brand because someone forgot the
 * provider would also silently drop the legal footer. THIS surface is the
 * exception, and the reason is what it renders - the stale-chunk notice, the
 * last thing on screen when very little else works. A boundary that crashed on
 * a missing provider would replace a recoverable page with a blank one, so the
 * throw is caught here and answered with `DefaultBranding`, the ONE file allowed
 * to hold the literal. The hook is still called unconditionally, so the hook
 * order is fixed.
 */
function useProductName(): string {
  try {
    return useBranding().branding.product_name || DefaultBranding.product_name;
  } catch {
    return DefaultBranding.product_name;
  }
}

// ---------------------------------------------------------------------------
// ErrorFallback
// ---------------------------------------------------------------------------

export interface ErrorFallbackProps {
  /** The caught error. Only its kind is shown; the raw message goes to the console. */
  error: Error;
  /** Resets the boundary. Used for ordinary errors; a chunk-load error reloads instead. */
  onReset: () => void;
}

/**
 * Shared fallback for error boundaries. Never renders `error.message` - it can
 * carry internal URLs and is untranslated (SPEC-FRONTEND-RULE-006).
 */
export function ErrorFallback({ error, onReset }: ErrorFallbackProps): React.ReactElement {
  const staleChunk = isChunkLoadError(error);
  const productName = useProductName();
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" color={staleChunk ? 'text.primary' : 'error'} sx={{ mb: 1 }}>
        {staleChunk ? (
          <Trans>{productName} has been updated</Trans>
        ) : (
          <Trans>Something went wrong</Trans>
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {staleChunk ? (
          <Trans>A new version is available. Reload the page to continue.</Trans>
        ) : (
          <Trans>An unexpected error occurred. Please try again.</Trans>
        )}
      </Typography>
      {staleChunk ? (
        <Button variant="contained" onClick={() => window.location.reload()}>
          <Trans>Reload page</Trans>
        </Button>
      ) : (
        <Button variant="contained" onClick={onReset}>
          <Trans>Try Again</Trans>
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// RouteErrorBoundary
// ---------------------------------------------------------------------------

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[RouteErrorBoundary] caught:', error, errorInfo);
    if (isChunkLoadError(error)) {
      reloadForStaleChunk();
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={this.handleRetry} />;
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
